import React, {useState, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {AccountTable} from './AccountTable.jsx';
import {AccountLines} from './AccountLines.jsx';
import {CheckboxWithOptionsAccountDetails} from './CheckboxWithOptionsAccountDetails.jsx';
import {Checkbox} from '@mui/material';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import SyncIcon from '@mui/icons-material/Sync';
import CheckIcon from '@mui/icons-material/Check';
import {ezprOrange} from '../../_helpers';
import {requestStores} from '../../ListOfPREditors.brick.jsx';
import {TablePaginationWithPageSelector} from '../../TablePaginationWithPageSelector.jsx';
import {getConsumptionLevelLambda} from '../../../utils/functionSignatures';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import produce from 'immer';
import {LAMBDA_NAMES} from '../../../utils/constants';
import {BrickContext} from 'olympe';
import {useFieldEditability} from '../utils/useFieldEditability.js';

/**
 * @param {AccountDetails.propTypes} props
 * @return {JSX.Element}
 */
export function AccountDetails(props) {
    const useRequestStore = requestStores.get(props.prTag);

    const getConsumptionLevel = props.context.get(LAMBDA_NAMES.GET_CONSUMPTION_LEVEL);
    const [consumptionLevelCheckDetails, setConsumptionLevelCheckDetails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [consumptionLevelCheckStatus, setConsumptionLevelCheckStatus] = useState('idle');
    const getPurchaseRequestFromStore = useRequestStore((state) => state.getPurchaseRequest);
    const [areAccountSplitByMaterial, setAreAccountSplitByMaterial] = useRequestStore((state) => [state.areAccountSplitByMaterial, state.setAreAccountSplitByMaterial]);
    const materials = useRequestStore((state) => state.materials);
    const [filteredAccounts, setFilteredAccounts] = useState([]);
    const [accountDetailsByMaterial, setAccountDetailsByMaterial] = useRequestStore((state) => [state.accountDetailsByMaterial, state.setAccountDetailsByMaterial]);
    const accounts = useRequestStore((state) => state.accounts);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [pageNum, setPageNum] = useState([]);
    const pageSize = rowsPerPage === -1 ? materials.length : (materials.length) / rowsPerPage;
    const setAllAccounts = useRequestStore((state) => state.setAllAccounts);
    const [selectedAccounts, setSelectedAccounts] = useState([]);

    const [modifiedAllocation, setModifiedAllocation] = useState({});
    const [resetAllocation, setResetAllocation] = useState({});

    const {isDisabled} = useFieldEditability(props.prTag, {context: props.context});

    const isThereWBSInAccounts = useMemo(() => {
        return areAccountSplitByMaterial ?
            accountDetailsByMaterial.some((adbm) =>
                adbm.accounts.some(
                    (a) => a.accountType.value === 'P' && a.wbs.value.startsWith('AC-'),
                ),
            ) :
            accounts.some(
                (a) => a.accountType.value === 'P' && a.wbs.value.startsWith('AC-'),
            );
    }, [areAccountSplitByMaterial, accountDetailsByMaterial, accounts]);

    useEffect(() => { // filter accounts when rowsperpage changes and update page number
        const temp = [];
        if (rowsPerPage === -1) {
            setFilteredAccounts(materials);
            setPage(0);
            temp.push(
                {
                    label: `1-${materials.length}`,
                    value: 1,
                },
            );
        } else {
            setFilteredAccounts(materials?.slice(rowsPerPage * page, rowsPerPage * (page + 1)));
            if (materials?.length > 0 && materials?.length <= page * rowsPerPage) {
                setPage((current) => current - 1);
            }
            for (let i = 0; i < pageSize; i++) {
                temp.push({
                    label: `${(i * rowsPerPage) + 1}-${(i + 1) * rowsPerPage < materials.length ? (i + 1) * rowsPerPage : materials.length}`,
                    value: i + 1,
                });
            }
        }
        return setPageNum(temp);
    }, [pageSize, rowsPerPage, page, materials]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handlePageSelection = (newValue) => {
        setPage(newValue);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleAreAccountSplitByMaterialChange = (_, checked) => {
        setAreAccountSplitByMaterial(checked);
    };

    const handleCopyAllocation = (materialIds, newValue) => {
        setModifiedAllocation({mIds: materialIds, val: newValue});
    };

    const handleResetAllocation = (materialIds) => {
        setResetAllocation({mIds: materialIds});
    };

    const handleConsumptionLevelCheck = () => {
        setLoading(true);
        setConsumptionLevelCheckStatus('loading');
        setConsumptionLevelCheckDetails([]);
        getConsumptionLevelLambda(props.context, getConsumptionLevel, JSON.stringify(getPurchaseRequestFromStore().getPurchaseRequest()), props.prTag, (result) => {
            if (result[0] && result[0].length > 0) {
                setConsumptionLevelCheckDetails(result[0]);
                setConsumptionLevelCheckStatus('error');
            } else {
                setConsumptionLevelCheckDetails([]);
                setConsumptionLevelCheckStatus('success');
            }
            setLoading(false);
        }, (e) => {
            console.error(e);
            setConsumptionLevelCheckDetails([]);
            setConsumptionLevelCheckStatus('error');
            setLoading(false);
        });
    };

    const getConsumptionLevelIcon = () => {
        if (loading) {
            return <CircularProgress size={20}/>;
        } else if (consumptionLevelCheckStatus === 'idle') {
            return <SyncIcon/>;
        } else if (consumptionLevelCheckStatus === 'success') {
            return <CheckIcon/>;
        } else if (consumptionLevelCheckStatus === 'error') {
            return <CloseIcon/>;
        }
    };

    const handleSelectAccount = (accountId, materialId, isSelected) => {
        setSelectedAccounts((previous) => {
            const identifier = {accountId, materialId};
            if (isSelected) {
                return [...previous, identifier];
            } else {
                return previous.filter((selection) => selection.accountId !== accountId || selection.materialId !== materialId);
            }
        });
    };

    const updateSelectedAccounts = (updates) => {
        if (areAccountSplitByMaterial) {
            const uniqueMaterialIds = [...new Set(selectedAccounts.map((selection) => selection.materialId))].sort((a, b) => a - b);

            const isAccountTypeUpdate = updates.hasOwnProperty('accountType');
            if (isAccountTypeUpdate) {
                uniqueMaterialIds.forEach((materialId) => {
                    const newAccountDetailsByMaterial = produce(accountDetailsByMaterial, (draft) => {
                        draft.find((item) => item.material_id === materialId).accounts.forEach((account) => account.accountType = updates['accountType'] ?? {
                            label: '',
                            value: '',
                        });
                    });
                    setAccountDetailsByMaterial(newAccountDetailsByMaterial, materialId);
                });
            } else {
                uniqueMaterialIds.forEach((materialId) => {
                    const accountsToUpdate = selectedAccounts.filter((account) => account.materialId === materialId);

                    if (accountsToUpdate.length > 0) {
                        const newAccountDetailsByMaterial = produce(accountDetailsByMaterial, (draft) => {
                            const materialEntry = draft.find((item) => item.material_id === materialId);
                            if (materialEntry) {
                                accountsToUpdate.forEach((account) => {
                                    if (account.accountId < materialEntry.accounts.length) {
                                        Object.keys(updates).forEach((key) => {
                                            materialEntry.accounts[account.accountId][key] = updates[key];
                                        });
                                    }
                                });
                            }
                        });
                        setAccountDetailsByMaterial(newAccountDetailsByMaterial, materialId);
                    }
                });
            }
        } else {
            const newAccounts = produce(accounts, (draft) => {
                draft.forEach((account, idx) => {
                    if (selectedAccounts.some((selectedAccount) => selectedAccount.accountId === idx)) {
                        Object.entries(updates).forEach(([property, value]) => {
                            account[property] = value;
                        });
                    }
                });
            });
            setAllAccounts(newAccounts);
        }
    };

    return (
        <div className={'pr-editor-centered-submodule'} style={{marginTop: '40px'}}>
            <div className="pr-editor-account-header">
                <div className={'pr-editor-account-header-title'}>
                    {areAccountSplitByMaterial &&
                        <CheckboxWithOptionsAccountDetails
                            prTag={props.prTag}
                            context={props.context}
                            selectedAccounts={selectedAccounts}
                            filteredAccounts={filteredAccounts}
                            setSelectedAccounts={setSelectedAccounts}
                            accountDetailsByMaterial={accountDetailsByMaterial}
                        />
                    }
                    <p className={'pr-editor-section-title'}>Account details</p>
                    {
                        isThereWBSInAccounts && !props.submittedRequestsTab ?
                            <Button
                                variant={'outlined'}
                                startIcon={getConsumptionLevelIcon()}
                                onClick={handleConsumptionLevelCheck}
                                sx={{
                                    'color': (consumptionLevelCheckStatus === 'success' ? '#4CAF50' : consumptionLevelCheckStatus === 'error' ? '#F44336' : ezprOrange),
                                    'borderColor': (consumptionLevelCheckStatus === 'success' ? '#4CAF50' : consumptionLevelCheckStatus === 'error' ? '#F44336' : ezprOrange),
                                    'border': '2px solid',
                                    'marginLeft': '2rem',
                                    '&:hover': {
                                        'borderColor': (consumptionLevelCheckStatus === 'success' ? '#4CAF50' : consumptionLevelCheckStatus === 'error' ? '#F44336' : ezprOrange),
                                        'border': '2px solid',
                                        'backgroundColor': 'transparent',
                                    },
                                }}
                            >
                                Check
                            </Button> : undefined
                    }
                </div>
                <div className={'pr-editor-account-checkbox'}>
                    <Checkbox
                        disabled={materials.length <= 1 || isDisabled('accountDetails.splitAccountByMaterial')}
                        checked={areAccountSplitByMaterial}
                        onChange={handleAreAccountSplitByMaterialChange}
                    />
                    <Typography>Different payment accounts for each item</Typography>
                </div>
            </div>
            {(areAccountSplitByMaterial) ?
                <div>
                    {filteredAccounts?.map((material) => {
                        return (<AccountTable
                            key={material.id}
                            materialId={material.id}
                            prTag={props.prTag}
                            context={props.context}
                            consumptionLevelCheckDetails={consumptionLevelCheckDetails}
                            consumptionLevelCheckStatus={consumptionLevelCheckStatus}
                            selectedAccounts={selectedAccounts}
                            onSelectAccount={handleSelectAccount}
                            updateSelectedAccounts={updateSelectedAccounts}
                            onSelectAllAccounts={setSelectedAccounts}
                            handleCopyAllocation={handleCopyAllocation}
                            modifiedAllocation={modifiedAllocation}
                            handleResetAllocation={handleResetAllocation}
                            resetAllocation={resetAllocation}
                        />);
                    })}
                    <TablePaginationWithPageSelector
                        page={page}
                        pageNum={pageNum}
                        count={materials.length}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[2, 5, 10, 20, 50, 100, {label: 'All', value: -1}]}
                        handlePageSelection={handlePageSelection}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                    />
                </div> :
                <AccountLines
                    prTag={props.prTag}
                    context={props.context}
                    consumptionLevelCheckDetails={consumptionLevelCheckDetails}
                    consumptionLevelCheckStatus={consumptionLevelCheckStatus}
                    selectedAccounts={selectedAccounts}
                    onSelectAccount={handleSelectAccount}
                    updateSelectedAccounts={updateSelectedAccounts}
                    onSelectAllAccounts={setSelectedAccounts}
                />
            }
        </div>
    );
}

AccountDetails.propTypes = {
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    submittedRequestsTab: PropTypes.bool,
};
